import { useCallback, useEffect, useState } from 'react';
import {
  useSearchParams,
  useLoaderData,
  useRevalidator,
} from 'react-router-dom';
import { IconButton, Skeleton, Snackbar, Snack } from '@nike/eds';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import DictionaryAlphabetFilter from '../../components/dictionaryAlphabetFilter';
import { extendedAlphabet } from '../../utils/alphabetHelpers';
import { QUERY_KEYS } from '../../constants';
import nicknameClientAPI, { NicknameGroup } from '../../clientAPI/nickname';
import propagate403Error from '../../utils/propagate403Error';
import {
  nickNameUpdateMessageAtom,
  nickNameUpdateHasErrorAtom,
} from '../../atoms/nickname';
import NicknameProductsEditModal from '../../components/Nickname/NicknameProductsEditModal';
import { isExistScope } from 'utils/authHelpers';

function NicknameLetterCard({
  letter,
  showEmptyState = false,
  onSettled,
  paginationSize,
  onSelectGroup,
}: {
  letter: string;
  showEmptyState?: boolean;
  onSettled?: () => void;
  paginationSize: number;
  onSelectGroup: (group: NicknameGroup) => void;
}) {
  const revalidator = useRevalidator();
  const [currentPage, setCurrentPage] = useState(1);

  const { status, data, error } = useQuery({
    queryKey: [
      QUERY_KEYS.NICKNAME_DICTIONARY,
      letter,
      currentPage,
      paginationSize,
    ],
    queryFn: () =>
      nicknameClientAPI.getNicknameList({
        indexKey: letter,
        pageSize: paginationSize,
        currentPage,
      }),
    staleTime: Infinity,
    onSettled,
    onError: (err: { status: number }) => {
      if (err.status === 401) revalidator.revalidate();
    },
  });

  propagate403Error(error as keyable);

  if (status === 'loading') {
    return <Skeleton height={300} width={220} />;
  }

  const groups = data?.groups ?? [];
  const hasPrev = currentPage > 1;
  const hasNext = data?.hasMore ?? false;
  const shouldPaginate = hasPrev || hasNext;

  if (groups.length === 0 && !hasPrev) {
    return showEmptyState ? (
      <h2 className='eds-type--display-5 tw-py-32 tw-text-center'>
        No data available
      </h2>
    ) : null;
  }

  return (
    <div className='tw-px-4 tw-py-3 tw-rounded tw-border-2 tw-border-slate-500 tw-bg-white'>
      <div className='tw-uppercase eds-type--subtitle-1 tw-text-left tw-mb-2'>
        {letter}
      </div>
      <div className='tw-text-left tw-space-y-2'>
        {groups.map((group) => (
          <div
            key={group.nickname}
            className='tw-group tw-border-b tw-border-slate-100 last:tw-border-0 tw-pb-1'
          >
            <div className='tw-flex tw-items-center tw-justify-between'>
              <span className='eds-type--body-2 tw-font-semibold tw-text-indigo-700'>
                {group.nickname}
              </span>
              <span className='tw-invisible group-hover:tw-visible'>
                <IconButton
                  icon='Edit'
                  size='small'
                  variant='ghost'
                  label={`Edit ${group.nickname}`}
                  onClick={() => onSelectGroup(group)}
                />
              </span>
            </div>
            <div className='tw-ml-4 tw-space-y-0.5'>
              {group.products.map((product) => (
                <div
                  key={product._id}
                  className='eds-type--body-3 tw-flex tw-items-baseline tw-gap-2'
                >
                  <span className='tw-text-slate-700'>
                    {product.copy?.title || product._id}
                  </span>
                  <span className='tw-text-xs tw-text-slate-400'>
                    {product.productCode}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {shouldPaginate && (
        <div className='tw-flex tw-items-center tw-justify-between tw-mt-4'>
          <IconButton
            icon='ArrowLeft'
            size='small'
            disabled={!hasPrev}
            onClick={() => setCurrentPage((p) => p - 1)}
            label='previous'
            variant='ghost'
          />
          <IconButton
            icon='ArrowRight'
            size='small'
            disabled={!hasNext}
            onClick={() => setCurrentPage((p) => p + 1)}
            label='next'
            variant='ghost'
          />
        </div>
      )}
    </div>
  );
}

export default function NicknameDictionary() {
  const [searchParams] = useSearchParams();
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:nickname:write', jwt);
  const [updateMsg, setUpdateMsg] = useAtom(nickNameUpdateMessageAtom);
  const [updateHasError] = useAtom(nickNameUpdateHasErrorAtom);
  const [selectedGroup, setSelectedGroup] = useState<NicknameGroup | null>(
    null,
  );
  const [isCreating, setIsCreating] = useState(false);

  const indexKeyFromUrl = (searchParams.get('indexKey') as string) || 'ALL';
  const isAll = indexKeyFromUrl === 'ALL';

  const letters = isAll ? extendedAlphabet : [indexKeyFromUrl];
  const paginationSize = isAll ? 10 : 100;

  const [visibleCount, setVisibleCount] = useState(1);

  useEffect(() => {
    setVisibleCount(1);
  }, [indexKeyFromUrl]);

  const advance = useCallback(() => setVisibleCount((n) => n + 2), []);

  const visibleLetters = isAll ? letters.slice(0, visibleCount) : letters;

  return (
    <section className='tw-text-center tw-py-8'>
      <DictionaryAlphabetFilter
        alphabet={extendedAlphabet}
        activeKey={indexKeyFromUrl}
      />

      <div className='tw-flex tw-flex-wrap tw-items-start tw-justify-center tw-gap-8'>
        {visibleLetters.map((letter, i) => (
          <NicknameLetterCard
            key={letter}
            letter={letter}
            showEmptyState={!isAll}
            onSettled={isAll && i === visibleCount - 1 ? advance : undefined}
            paginationSize={paginationSize}
            onSelectGroup={setSelectedGroup}
          />
        ))}
      </div>

      <NicknameProductsEditModal
        group={selectedGroup}
        onClose={() => {
          setSelectedGroup(null);
          setIsCreating(false);
        }}
        isCreating={isCreating}
        readOnly={!canWrite}
      />

      {canWrite && (
        <div className='tw-fixed tw-bottom-10 tw-right-20'>
          <IconButton
            icon='Plus'
            label='New'
            onClick={() => setIsCreating(true)}
          />
        </div>
      )}

      <Snackbar style={{ left: '90%', top: '90%' }}>
        {updateMsg && (
          <Snack
            autoDismissDuration={5000}
            id='nickname-dictionary-notification'
            onDismiss={() => {
              setUpdateMsg('');
            }}
            status={updateHasError ? 'error' : 'success'}
          >
            {updateMsg}
          </Snack>
        )}
      </Snackbar>
    </section>
  );
}
